// Auto visual — picks the visual type from the fields the author has placed and
// lays them into that type's drop zones (ColumnGroups).
//
// Rules are deliberately few and explainable (the reason is shown to the
// author, who can keep the pick or try another). They read only what the field
// bank already knows: whether a column is a measure, a date or a coordinate.
// Every visual named here exists in the catalog; a name the catalog lacks is
// never returned (the caller keeps the current type).

import type { BankColumn } from './dataSource'
import type { ColumnGroupSpec } from './catalog'

export type FieldRole = 'measure' | 'date' | 'geo' | 'dimension'

export interface AutoField {
  key: string
  label: string
  role: FieldRole
}

export function roleOf(b: BankColumn): FieldRole {
  const n = `${b.DBNAME || ''} ${b.name || ''}`.toLowerCase()
  if (/(^|[\s_])(lat|latitude|lon|lng|longitude)([\s_]|$)/.test(n)) return 'geo'
  if (b.isAggregate || (b.defaultFunction || '').trim()) return 'measure'
  if (b.type === 'number') return 'measure'
  if (b.type === 'date') return 'date'
  return 'dimension'
}

export function toAutoField(b: BankColumn): AutoField {
  return { key: b.key, label: b.label || b.name, role: roleOf(b) }
}

const isTarget = (f: AutoField) => /target|budget|plan|quota/i.test(f.label)
const isStage = (f: AutoField) => /stage|status|funnel|step/i.test(f.label)

/** Pick the visual for these fields. `available` is the catalog's VisualNames. */
export function chooseVisual(
  fields: AutoField[], available: string[], current: string,
): { visual: string; reason: string } {
  const has = (n: string) => available.includes(n)
  const pick = (n: string, reason: string, fallback = current) =>
    (has(n) ? { visual: n, reason } : { visual: fallback, reason: '' })
  const dims = fields.filter((f) => f.role === 'dimension' || f.role === 'date')
  const meas = fields.filter((f) => f.role === 'measure')
  const geo = fields.filter((f) => f.role === 'geo')
  const date = dims.find((f) => f.role === 'date')
  const hasTarget = meas.length >= 2 && meas.some(isTarget)

  if (!fields.length) return { visual: current, reason: '' }
  if (geo.length) return pick('Map', 'there are location fields')
  if (!dims.length) {
    if (hasTarget && meas.length === 2) return pick('Gauge', 'a value and its target')
    return pick('Card', meas.length === 1 ? 'one number on its own reads best big' : 'numbers with no breakdown')
  }
  if (!meas.length) return pick('Grid', 'only descriptive fields — a list')
  if (dims.length >= 3 || (dims.length >= 2 && meas.length >= 2)) return pick('Pivot', 'several breakdowns and numbers')
  if (dims.length === 2) {
    if (date) {
      const other = dims.find((f) => f !== date)
      return pick('Line', `${date.label} is a date — one line per ${other ? other.label : 'value'}`)
    }
    return pick('Stacked Column', `${dims[0].label} split by ${dims[1].label}`)
  }
  const d = dims[0]
  if (d.role === 'date') {
    return meas.length > 1
      ? pick('Column Line', `${d.label} is a date, with two numbers`)
      : pick('Line', `${d.label} is a date — shown as a trend`)
  }
  if (isStage(d)) return pick('Funnel', `${d.label} is a sequence of stages`)
  if (hasTarget) return pick('Column Line', 'actuals as columns, target as a line')
  if (meas.length === 3) return pick('Bubble Chart', `three numbers per ${d.label}`)
  if (meas.length === 2) return pick('Column', `two numbers side by side per ${d.label}`)
  return pick('Column', `compares ${meas[0].label} across ${d.label}`)
}

/** Suggestions for the toolbar's quick picks — a short list that fits the
 *  fields. The list depends on the fields only (never on the current type), so
 *  the buttons stay put when one is clicked; the caller marks the current one. */
export function suggestVisuals(fields: AutoField[], available: string[]): { visual: string; why: string }[] {
  const dims = fields.filter((f) => f.role === 'dimension' || f.role === 'date')
  const meas = fields.filter((f) => f.role === 'measure')
  let list: [string, string][]
  if (!dims.length) list = [['Card', 'one number, big'], ['Gauge', 'progress against a target'], ['DoughNut', 'achievement rings']]
  else if (dims.some((f) => f.role === 'date')) list = [['Line', 'a trend over time'], ['Column', 'compare periods'], ['Waterfall', 'change period to period']]
  else if (dims.length >= 2) list = [['Stacked Column', 'parts of each total'], ['Heatmap', 'two fields cross-tabbed'], ['Pivot', 'rows × columns table']]
  else if (meas.length >= 2) list = [['Column', 'compare side by side'], ['Column Line', 'bars plus a line'], ['Scatter', 'one against another']]
  else list = [['Column', 'compare categories'], ['Bar', 'long labels read better'], ['Pie', 'share of a whole']]
  return list.filter(([n]) => available.includes(n)).map(([visual, why]) => ({ visual, why }))
}

/**
 * Carry the placed fields over to another visual type's groups.
 *
 * When the new type has the SAME drop zones as the old one (same
 * StandardNames, any case — Column, Bar, Line, Pie…), nothing moves: each
 * field stays in its zone, in order, up to the zone's limit, and a field over
 * a limit goes to the best free zone that takes it. Otherwise a zone name
 * shared by chance means nothing (Grid's "Columns" are the table's columns,
 * Pie's are its series split), so the fields are laid out afresh exactly as
 * Auto would (`assignToGroups`), in their current order. `dropped` lists the
 * fields no zone could take.
 */
export function carryOver(
  groups: ColumnGroupSpec[],
  placed: { std: string; field: AutoField }[],
  fromZones: string[],
): { plan: Record<string, string[]>; dropped: AutoField[] } {
  const plan: Record<string, string[]> = {}
  for (const g of groups) plan[g.StandardName] = []
  const zoneSet = (names: string[]) => [...new Set(names.map((n) => n.toLowerCase()))].sort().join('|')
  if (zoneSet(fromZones) !== zoneSet(groups.map((g) => g.StandardName))) {
    const auto = assignToGroups(groups, placed.map((p) => p.field))
    const got = new Set(Object.values(auto).flat())
    return { plan: auto, dropped: placed.map((p) => p.field).filter((f) => !got.has(f.key)) }
  }
  const byLower = new Map(groups.map((g) => [g.StandardName.toLowerCase(), g]))
  const limit = (g: ColumnGroupSpec) => Number(g.MaxColumns || 0)
  const room = (g: ColumnGroupSpec) => !limit(g) || plan[g.StandardName].length < limit(g)
  const left: AutoField[] = []
  for (const p of placed) {
    const g = byLower.get(p.std.toLowerCase())
    if (g && room(g)) plan[g.StandardName].push(p.field.key)
    else left.push(p.field)
  }
  const valueLike = (g: ColumnGroupSpec) => /value|size|stack|lines|target|achievement/i.test(g.StandardName)
  const dropped: AutoField[] = []
  for (const f of left) {
    const fits = (g: ColumnGroupSpec) => (f.role === 'measure' ? valueLike(g) : !valueLike(g) && !/^(min|max)$/i.test(g.StandardName))
    const target = groups.find((g) => accepts(g, f) && room(g) && fits(g) && !plan[g.StandardName].length)
      || groups.find((g) => accepts(g, f) && room(g) && fits(g))
      // Min / Max set a gauge's scale — never a place to park a stray field.
      || groups.find((g) => accepts(g, f) && room(g) && !/^(min|max)$/i.test(g.StandardName))
    if (target) plan[target.StandardName].push(f.key)
    else dropped.push(f)
  }
  return { plan, dropped }
}

const accepts = (g: ColumnGroupSpec, f: AutoField) => {
  const t = (g.AllowedColumnTypes || 'ANY').toUpperCase()
  if (t === 'NUMBER') return f.role === 'measure' || f.role === 'geo'
  if (t === 'DATE') return f.role === 'date'
  if (t === 'STRING') return f.role === 'dimension'
  return true
}

/**
 * Lay the fields into the chosen type's groups. Returns StandardName → field
 * keys. Known shapes get explicit placement (dates first on the axis, a target
 * measure on the line); anything else fills groups in catalog order by role.
 */
export function assignToGroups(groups: ColumnGroupSpec[], fields: AutoField[]): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const g of groups) out[g.StandardName] = []
  const std = (name: string) => groups.find((g) => g.StandardName.toLowerCase() === name.toLowerCase())?.StandardName
  let dims = fields.filter((f) => f.role === 'dimension' || f.role === 'date')
  const date = dims.find((f) => f.role === 'date')
  if (date) dims = [date, ...dims.filter((f) => f !== date)]
  const meas = fields.filter((f) => f.role === 'measure')
  const geo = fields.filter((f) => f.role === 'geo')
  const tgt = meas.find(isTarget)
  const nonT = meas.filter((f) => f !== tgt)
  const used = new Set<string>()
  const put = (name: string | undefined, list: AutoField[]) => {
    if (!name) return
    for (const f of list) { if (!used.has(f.key)) { out[name].push(f.key); used.add(f.key) } }
  }

  const rows = std('Rows'), cols = std('Columns'), vals = std('Values')
  if (rows && vals) {
    // Rows / Columns / Values family (charts, pivot, heatmap…).
    const twoLevelRows = !!std('Groups') || /pivot|task/i.test(groups.map((g) => g.GroupName).join(' '))
    if (cols && dims.length >= 2) {
      put(rows, dims.slice(0, twoLevelRows && dims.length > 2 ? 2 : 1))
      put(cols, dims.filter((f) => !used.has(f.key)).slice(0, 1))
    } else put(rows, dims.slice(0, 1))
    put(vals, tgt && nonT.length ? [...nonT, tgt] : meas)
  } else if (std('Value') && std('Target') && std('Min')) {
    // Gauge.
    put(std('Value'), (nonT.length ? nonT : meas).slice(0, 1))
    if (tgt && nonT.length) put(std('Target'), [tgt])
  } else if (std('CenterFirstRow') && std('TopLeft')) {
    // Card / Map pop-up.
    put(std('Latitude'), geo.filter((f) => /lat/i.test(f.label)))
    put(std('Longitude'), geo.filter((f) => /lon|lng/i.test(f.label)))
    put(std('Category'), dims.slice(0, 1))
    put(std('Size'), meas.slice(0, 1))
    put(std('CenterFirstRow'), meas.filter((f) => !used.has(f.key)).slice(0, 1))
    put(std('TopLeft'), dims.filter((f) => !used.has(f.key)).slice(0, 1))
    put(std('TopRight'), meas.filter((f) => !used.has(f.key)).slice(0, 1))
    put(std('CenterSecondRow'), dims.filter((f) => !used.has(f.key)).slice(0, 1))
    put(std('BottomRight'), meas.filter((f) => !used.has(f.key)).slice(0, 1))
  }

  // Everything still unplaced: fill groups in catalog order by role. A group
  // with no limit that takes "every field" (Grid/Tabular Columns, Document
  // Fields) gets all the rest.
  const rest = fields.filter((f) => !used.has(f.key))
  if (groups.length === 1) { put(groups[0].StandardName, rest); return out }
  for (const f of rest) {
    const room = (g: ColumnGroupSpec) => {
      const max = Number(g.MaxColumns || 0)
      return !max || out[g.StandardName].length < max
    }
    const wantNum = f.role === 'measure'
    const target = groups.find((g) => accepts(g, f) && room(g) && !out[g.StandardName].length
      && (wantNum ? /value|size|stack|lines|target|achievement/i.test(g.StandardName)
        : !/value|size|stack|lines|achievement|min|max/i.test(g.StandardName)))
    if (target) { out[target.StandardName].push(f.key); used.add(f.key) }
  }
  return out
}
